import React, { useState, useEffect, useCallback } from 'react';
import { Product, apiService } from '../../services/api';
import ProductModal from '../../components/ProductModal';
import ConfirmModal from '../../components/admin/ConfirmModal';
import SuccessModal from '../../components/admin/SuccessModal';
import Select2 from '../../components/Select2';
import { ArrowDownTrayIcon, ChevronDownIcon, PlusIcon, MagnifyingGlassIcon, EyeIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';



const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [brandFilter, setBrandFilter] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Helper function to calculate total stock from variants
  const calculateTotalStock = (product: Product): number => {
    if (!product.variants || product.variants.length === 0) {
      return product.in_stock || 0;
    }
    
    return product.variants.reduce((total, variant) => {
      if (!variant.sizes || variant.sizes.length === 0) {
        return total;
      }
      
      const variantStock = variant.sizes.reduce((sizeTotal, size) => {
        return sizeTotal + (size.stock_quantity || 0);
      }, 0);
      
      return total + variantStock;
    }, 0);
  };
  
  // Modal states
  const [productModal, setProductModal] = useState({ 
    isOpen: false, 
    mode: 'create' as 'create' | 'edit' | 'view', 
    product: undefined as Product | undefined 
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
  const [successModal, setSuccessModal] = useState({ 
    isOpen: false, 
    title: '', 
    message: '' 
  });

  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await apiService.get('/admin/products', {
        params: {
          search: debouncedSearchTerm,
          category_id: categoryFilter || undefined,
          brand_id: brandFilter || undefined,
          per_page: itemsPerPage.toString(),
          page: currentPage.toString()
        }
      });
      
      setProducts(response.data || []);
      setTotalPages(response.last_page || 1);
      setTotalItems(response.total || 0);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, categoryFilter, brandFilter, currentPage, itemsPerPage]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiService.get('/admin/categories/hierarchical');
      setCategories(data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      const data = await apiService.get('/admin/brands', { 
        params: { 
          per_page: 100, 
          _t: Date.now() 
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      setBrands(data.data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  }, []);

  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Get page numbers for pagination
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }, [currentPage, totalPages]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, [fetchCategories, fetchBrands]);

  // Reset pagination when search term changes
  useEffect(() => {
    resetPagination();
  }, [debouncedSearchTerm, resetPagination]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (exportDropdownOpen && !target.closest('.export-dropdown')) {
        setExportDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportDropdownOpen]);

  const handleCreateProduct = () => {
    setProductModal({ isOpen: true, mode: 'create', product: undefined });
  };

  const handleViewProduct = async (product: Product) => {
    try {
      // Fetch full product details with variants
      const fullProduct = await apiService.get(`/admin/products/${product.id}`);
      setProductModal({ isOpen: true, mode: 'view', product: fullProduct });
    } catch (error) {
      console.error('Error fetching product details:', error);
      alert('Failed to load product details. Please try again.');
    }
  };

  const handleEditProduct = (product: Product) => {
    setProductModal({ isOpen: true, mode: 'edit', product });
  };

  const handleDeleteProduct = (product: Product) => {
    setConfirmMessage(`Are you sure you want to delete "${product.name}"? This action cannot be undone.`);
    setConfirmAction(() => () => deleteProduct(product.id));
    setShowConfirmModal(true);
  };

  const deleteProduct = async (productId: number) => {
    try {
      await apiService.delete(`/admin/products/${productId}`);
      
      await fetchProducts();
      
      setSuccessModal({
        isOpen: true,
        title: 'Success',
        message: 'Product deleted successfully!'
      });
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
    } finally {
      setShowConfirmModal(false);
    }
  };

  const handleSaveProduct = async (productData: any) => {
    try {
      if (productModal.mode === 'create') {
        await apiService.post('/admin/products', productData);
        setSuccessModal({
          isOpen: true,
          title: 'Success',
          message: 'Product created successfully!'
        });
      } else if (productModal.mode === 'edit' && productModal.product) {
        await apiService.put(`/admin/products/${productModal.product.id}`, productData);
        setSuccessModal({
          isOpen: true,
          title: 'Success',
          message: 'Product updated successfully!'
        });
      }
      
      await fetchProducts();
      setProductModal({ isOpen: false, mode: 'create', product: undefined });
    } catch (error: any) {
      console.error('Error saving product:', error);
      alert(`Failed to save product: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCloseSuccessModal = useCallback(() => {
    setSuccessModal({ isOpen: false, title: '', message: '' });
  }, []);

  // Export functions
  const downloadFile = (data: any[], filename: string, format: 'csv' | 'excel') => {
    if (format === 'csv') {
      // Convert array to CSV
      const csvContent = data.map(row => 
        row.map((field: any) => {
          // Handle fields that might contain commas or quotes
          const fieldStr = String(field || '');
          if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
            return `"${fieldStr.replace(/"/g, '""')}"`;
          }
          return fieldStr;
        }).join(',')
      ).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (format === 'excel') {
      // For Excel, we'll use a simple HTML table approach that Excel can read
      const htmlTable = `
        <table>
          ${data.map(row => 
            `<tr>${row.map((cell: any) => `<td>${String(cell || '')}</td>`).join('')}</tr>`
          ).join('')}
        </table>
      `;
      
      const blob = new Blob([htmlTable], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setExportLoading(true);
      setExportDropdownOpen(false);
      
      const response = await apiService.get('/admin/products/export', {
        params: {
          format,
          search: searchTerm,
          category_id: categoryFilter || undefined,
        }
      });
      
      if (response.data && response.filename) {
        downloadFile(response.data, response.filename, format);
        
        setSuccessModal({
          isOpen: true,
          title: 'Export Successful',
          message: `Successfully exported ${response.count || 0} products to ${format.toUpperCase()} format.`
        });
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Failed to export products to ${format.toUpperCase()}. Please try again.`);
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Products</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your product catalog
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Export Dropdown */}
          <div className="relative export-dropdown">
            <button
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              disabled={exportLoading}
              className="bg-gray-500 text-white px-3 py-1.5 text-sm rounded-md hover:bg-gray-600 flex items-center space-x-1.5 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span>{exportLoading ? 'Exporting...' : 'Export'}</span>
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            
            {exportDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                <div className="py-1">
                  <button
                    onClick={() => handleExport('csv')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-2">
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <span>Export as CSV</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleExport('excel')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-2">
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <span>Export as Excel</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button 
            onClick={handleCreateProduct}
            className="bg-gray-500 text-white px-3 py-1.5 text-sm rounded-md hover:bg-gray-600 flex items-center space-x-1.5"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Add Product</span>
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search Input */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <Select2
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as 'all' | 'active' | 'inactive');
                resetPagination();
                // Clear other filters when status is selected
                if (value !== 'all') {
                  setCategoryFilter('');
                  setBrandFilter('');
                }
              }}
              placeholder="Select status..."
              isClearable={false}
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <Select2
              options={[
                { value: '', label: 'All Categories' },
                ...categories.map(category => ({
                  value: category.id,
                  label: category.level === 0 
                    ? category.name 
                    : `└─ ${category.name}`
                }))
              ]}
              value={categoryFilter || ''}
              onChange={(value) => {
                setCategoryFilter(value === '' ? '' : value?.toString() || '');
                resetPagination();
                // Clear other filters when category is selected
                if (value && value !== '') {
                  setStatusFilter('all');
                  setBrandFilter('');
                }
              }}
              placeholder="Select category..."
              isSearchable={true}
            />
          </div>

          {/* Brand Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
            <Select2
              options={[
                { value: '', label: 'All Brands' },
                ...brands.map(brand => ({
                  value: brand.id,
                  label: brand.name
                }))
              ]}
              value={brandFilter || ''}
              onChange={(value) => {
                setBrandFilter(value === '' ? '' : value?.toString() || '');
                resetPagination();
                // Clear other filters when brand is selected
                if (value && value !== '') {
                  setStatusFilter('all');
                  setCategoryFilter('');
                }
              }}
              placeholder="Select brand..."
              isSearchable={true}
            />
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {totalItems > 0 ? (
              <>Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} products</>
            ) : (
              'No products found'
            )}
          </div>
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setCategoryFilter('');
              setBrandFilter('');
              resetPagination();
            }}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98] active:bg-gray-100"
          >
            <XMarkIcon className="h-4 w-4" />
            <span>Clear Filters</span>
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed" style={{ minWidth: '1200px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Brand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40 min-w-[160px]">
                  Actions
                </th>
              </tr>
            </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product: Product) => (
              <tr key={product.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12">
                      <img
                        className="h-12 w-12 rounded-lg object-cover"
                        src={product.product_images?.[0] || '/placeholder-image.jpg'}
                        alt={product.name}
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {product.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {product.slug}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {product.product_code}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {typeof product.category === 'object' && product.category?.name 
                        ? product.category.name 
                        : typeof product.category === 'string' 
                        ? product.category 
                        : 'N/A'}
                    </span>
                    {typeof product.category === 'object' && product.category?.parent && (
                      <span className="text-xs text-gray-400">
                        {product.category.parent.name} → {product.category.name}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {product.brand?.name || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {product.has_promotion && product.discounted_price ? (
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <span className="text-red-600 font-medium">
                          {product.discounted_price.toLocaleString()} Ks
                        </span>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          -{product.discount_percentage?.toFixed(0)}%
                        </span>
                      </div>
                      <span className="text-gray-400 line-through text-xs">
                        {product.price.toLocaleString()} Ks
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-900 font-medium">
                      {product.price.toLocaleString()} Ks
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={calculateTotalStock(product) > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                    {calculateTotalStock(product)} units
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    product.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-40 min-w-[160px]">
                  <div className="flex items-center justify-start space-x-2">
                    <button 
                      onClick={() => handleViewProduct(product)}
                      className="text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 p-2 rounded transition-colors flex-shrink-0"
                      title="View Product"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleEditProduct(product)}
                      className="text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 p-2 rounded transition-colors flex-shrink-0"
                      title="Edit Product"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteProduct(product)}
                      className="text-red-600 hover:text-red-900 hover:bg-red-50 p-2 rounded transition-colors flex-shrink-0"
                      title="Delete Product"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="lg:hidden">
          {products.map((product: Product) => (
            <div key={product.id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
              <div className="flex items-start space-x-4">
                {/* Product Image */}
                <div className="flex-shrink-0">
                  <img
                    className="h-16 w-16 rounded-lg object-cover"
                    src={product.product_images?.[0] || '/placeholder-image.jpg'}
                    alt={product.name}
                  />
                </div>
                
                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {product.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {product.slug}
                      </p>
                      
                      {/* Product Code */}
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {product.product_code}
                        </span>
                      </div>
                      
                      {/* Category & Brand */}
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                        <span>
                          <strong>Category:</strong> {typeof product.category === 'object' && product.category?.name 
                            ? product.category.name 
                            : typeof product.category === 'string' 
                            ? product.category 
                            : 'N/A'}
                        </span>
                        <span>•</span>
                        <span>
                          <strong>Brand:</strong> {product.brand?.name || 'N/A'}
                        </span>
                      </div>
                      
                      {/* Price */}
                      <div className="mt-2">
                        {product.has_promotion && product.discounted_price ? (
                          <div className="flex flex-col">
                            <div className="flex items-center space-x-2">
                              <span className="text-red-600 font-medium text-sm">
                                {product.discounted_price.toLocaleString()} Ks
                              </span>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                -{product.discount_percentage?.toFixed(0)}%
                              </span>
                              {product.promotional_price && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  SAVED
                                </span>
                              )}
                            </div>
                            <span className="text-gray-400 line-through text-xs">
                              {product.price.toLocaleString()} Ks
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-900 font-medium text-sm">
                            {product.price.toLocaleString()} Ks
                          </span>
                        )}
                      </div>
                      
                      {/* Stock & Status */}
                      <div className="mt-2 flex items-center space-x-4">
                        <div className="flex items-center">
                          <span className="text-xs text-gray-500 mr-1">Stock:</span>
                          <span className={`text-xs font-medium ${calculateTotalStock(product) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {calculateTotalStock(product)} units
                          </span>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          product.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col space-y-2 ml-2">
                      <button 
                        onClick={() => handleViewProduct(product)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleEditProduct(product)}
                        className="text-yellow-600 hover:text-yellow-900 p-1"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteProduct(product)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {totalItems === 0 ? 'No products available.' : 'No products found matching your criteria.'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        {/* Mobile Pagination */}
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || totalPages <= 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98]"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages <= 1}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98]"
          >
            Next
          </button>
        </div>
        
        {/* Desktop Pagination */}
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              {totalItems > 0 ? (
                <>
                  Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                  <span className="font-medium">{totalItems}</span> results
                </>
              ) : (
                'No results found'
              )}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || totalPages <= 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98]"
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Page Numbers - Show even for single page */}
              {totalPages > 0 && getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  disabled={totalPages <= 1}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-200 ease-in-out hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-100 active:scale-[0.98] ${
                    page === currentPage
                      ? 'z-10 bg-gray-500 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400'
                  } ${totalPages <= 1 ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  {page}
                </button>
              ))}
              
              {/* Show page 1 if no pages calculated */}
              {totalPages === 0 && (
                <button
                  disabled
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 cursor-not-allowed opacity-50"
                >
                  1
                </button>
              )}
              
              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages <= 1}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98]"
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProductModal
        isOpen={productModal.isOpen}
        onClose={() => setProductModal({ isOpen: false, mode: 'create', product: undefined })}
        onSave={handleSaveProduct}
        mode={productModal.mode}
        product={productModal.product}
      />

      {showConfirmModal && (
        <ConfirmModal
          message={confirmMessage}
          onConfirm={() => {
            confirmAction();
            setShowConfirmModal(false);
          }}
          onCancel={() => setShowConfirmModal(false)}
          type="danger"
          confirmText="Delete"
        />
      )}

      {successModal.isOpen && (
        <SuccessModal
          onClose={handleCloseSuccessModal}
          title={successModal.title}
          message={successModal.message}
        />
      )}
    </div>
  );
};

export default AdminProducts;
